'use client'

import { useEffect } from 'react'
import Lenis from 'lenis'

/**
 * Lenis smooth scroll wrapper.
 *
 * Intercepte le scroll natif (wheel + touch) et y applique de l'easing
 * via une boucle rAF. Effet :
 *  - Scroll perçu plus "buttery" / premium (style luxe)
 *  - Vitesse effective de scroll cappée par la courbe easing → annule
 *    le visible lag du clip-path JS sur mobile (image bg du slider)
 *  - Compositor-driven (uses transform on body)
 *
 * `syncTouch` active le smooth scroll sur mobile aussi (sinon Lenis ne
 * touche que wheel/keyboard sur desktop).
 */
export default function LenisProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const lenis = new Lenis({
      // Durée et easing : plus le duration est haut, plus le scroll est
      // "lent à se poser" → vitesse effective plus basse. 1.2s = sweet spot
      // entre fluidité luxe et réactivité.
      duration: 1.2,
      // easeOutExpo — courbe luxe utilisée par Locomotive/Lenis demos
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      // Smooth scroll partout
      smoothWheel: true,
      // Active aussi sur les events touch (mobile) — clé pour notre use case
      syncTouch: true,
      // Multiplier pour la sensitivity touch (1 = défaut, plus bas = plus lent)
      touchMultiplier: 1.2,
      // Garde le scroll natif visible dans la scrollbar (sinon scrollbar n'apparait pas)
      autoRaf: false,
    })

    /* Expose globalement pour que d'autres composants (BrixsaHeader auto-hide
       burger sur mobile, animations scroll-driven) puissent s'abonner aux
       events Lenis — nécessaire car syncTouch hijack les events touch et
       window.scrollY ne se met pas à jour sur mobile. */
    ;(window as Window & { __ldcLenis?: typeof lenis }).__ldcLenis = lenis

    let rafId: number
    function raf(time: number) {
      lenis.raf(time)
      rafId = requestAnimationFrame(raf)
    }
    rafId = requestAnimationFrame(raf)

    return () => {
      cancelAnimationFrame(rafId)
      lenis.destroy()
      delete (window as Window & { __ldcLenis?: typeof lenis }).__ldcLenis
    }
  }, [])

  return <>{children}</>
}
